import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { isAdmin } from '@/api/auth'
import { trackPageview } from '@/lib/analytics'

// Public routes are eager-ish; the admin bundle is split out so it is only
// fetched for admins. Access to data is enforced server-side by the API routes
// in staticwebapp.config.json + the BFF; the meta flags below are for UX routing.
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/sites/:id',
    name: 'site',
    component: () => import('@/views/SiteDetailView.vue'),
    props: true,
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/favorites',
    name: 'favorites',
    component: () => import('@/views/FavoritesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/foreslaa-sted',
    name: 'submit-site',
    component: () => import('@/views/SubmitSiteView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Public: unit prefs are useful for everyone; the notifications section self-gates on auth.
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
  },
  {
    // Public: privatlivsnoten skal kunne læses uden at logge ind.
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminHomeView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/dykkersteder',
    name: 'admin-sites',
    component: () => import('@/views/AdminSitesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    // Foer :id-ruten: 'nyt' er et statisk segment og maa ikke opfattes som et sted-id.
    path: '/admin/dykkersteder/nyt',
    name: 'admin-site-new',
    component: () => import('@/views/AdminSiteCreateView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/dykkersteder/:id',
    name: 'admin-site-edit',
    component: () => import('@/views/AdminSiteEditView.vue'),
    props: true,
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/brugere',
    name: 'admin-users',
    component: () => import('@/views/AdminUsersView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/sigtmeldinger',
    name: 'admin-reports',
    component: () => import('@/views/AdminReportsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/opdagelser',
    name: 'admin-discoveries',
    component: () => import('@/views/AdminDiscoveriesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/aktivitet',
    name: 'admin-activity',
    component: () => import('@/views/AdminActivityView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/statistik',
    name: 'admin-stats',
    component: () => import('@/views/AdminStatsView.vue'),
    meta: { requiresAdmin: true },
  },
  // Gammelt bogmaerke fra da aktivitetssiden hed /admin/activity.
  { path: '/admin/activity', redirect: '/admin/aktivitet' },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    // Ankre (fx /about#installer fra install-nudgen) skal lande paa deres sektion.
    if (to.hash) return { el: to.hash, top: 16 }
    return savedPosition ?? { top: 0 }
  },
})

// Send a signed-out visitor who lands directly on a protected route to /login,
// carrying the original path so they return there after signing in. (SWA's static
// 401 redirect can't preserve the path.) The API endpoints stay server-enforced.
router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth && !to.meta.requiresAdmin) return true

  const { principal, isAuthenticated, load } = useAuth()
  await load()

  if (!isAuthenticated.value) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  // Signed in but not an administrator → send home (in dev the mock user has no role).
  if (to.meta.requiresAdmin && !import.meta.env.DEV && !isAdmin(principal.value)) {
    return { path: '/' }
  }
  return true
})

// Besøgsstatistik: hver gennemført navigation meldes anonymt (se lib/analytics.ts).
// afterEach, ikke beforeEach — en afvist eller omdirigeret navigation er ikke en visning.
router.afterEach((to) => trackPageview(to))
